import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import userApi from '@bo-apis/user/userApi';
import SearchUserPopup from '@bo-components/common/components/user/SearchUserPopup';
import Autocomplete from '@bo-components/common/autocomplete/Autocomplete';

const SearchUserWrap = styled.div`
  width: 100%;
`;

function SearchUserAutocomplete({
  onInputChange,
  onSelect,
  onClose,
  userId,
  userName,
  disabled = false,
  error,
  errorMessage,
}) {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [userIdNameList, setUserIdNameList] = useState([]);
  const [isOpenUserSearchPopup, setIsOpenUserSearchPopup] = useState(false);

  const fetchUserIdNameList = async () => {
    const { result } = await userApi.getUserList();
    if (!result) return;

    setUserIdNameList(
      result.map(({ userId, userName }) => ({
        userId,
        userName,
        itemLabel: `${userName}/${userId}`,
      }))
    );
  };

  const handleSearchInputChange = useCallback(
    (e) => {
      const inputValue = e?.target?.value.trim();
      setSearchKeyword(inputValue);
      onInputChange?.(inputValue);

      if (e?.target?.value?.length === 0) {
        onSelect?.({});
        setSelectedUserId('');
        setSelectedUserName('');
      }
    },
    [onInputChange, onSelect]
  );

  const handleInputItemClick = useCallback(
    (name, item) => {
      if (!name || !item) return;

      const { userId, userName } = item;
      onSelect?.({ userId, userName });
      setSelectedUserId(userId);
      setSelectedUserName(userName);
    },
    [onSelect]
  );

  const setInputValueSearchKeyword = useCallback((value) => {
    setSearchKeyword(value);
  }, []);

  useEffect(() => {
    (async () => {
      await fetchUserIdNameList();
    })();
  }, []);

  useEffect(() => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    if (userId || userName) {
      setSearchKeyword(`${userName}/${userId}`);
    } else {
      setSearchKeyword('');
    }
  }, [userId, userName]);

  const openUserSearchPopup = useCallback(() => setIsOpenUserSearchPopup(true), []);

  const handleClosePopup = useCallback(() => {
    onClose?.();
    setIsOpenUserSearchPopup(false);
  }, [onClose]);

  const handleConfirmPopup = useCallback(
    ({ userId, userName }) => {
      onSelect?.({ userId, userName });

      setSelectedUserId(userId);
      setSelectedUserName(userName);
      setIsOpenUserSearchPopup(false);
      setSearchKeyword(`${userName}/${userId}`);
    },
    [onSelect]
  );

  const handleBlur = useCallback(() => {
    if (selectedUserName || selectedUserId) {
      setSearchKeyword(`${selectedUserName}/${selectedUserId}`);
    } else {
      setSearchKeyword('');
    }
  }, [selectedUserName, selectedUserId]);

  return (
    <>
      <SearchUserWrap onBlur={handleBlur}>
        <Autocomplete
          placeholder={'사용자명/사용자ID'}
          onChange={handleSearchInputChange}
          value={searchKeyword}
          setValue={setInputValueSearchKeyword}
          items={userIdNameList}
          handleSearchClick={openUserSearchPopup}
          handleItemClick={handleInputItemClick}
          disabled={disabled}
          error={error}
          errorMessage={errorMessage}
        />
      </SearchUserWrap>
      <SearchUserPopup open={isOpenUserSearchPopup} onClose={handleClosePopup} onConfirm={handleConfirmPopup} />
    </>
  );
}

SearchUserAutocomplete.defaultProps = {
  userId: '',
  userName: '',
};

SearchUserAutocomplete.propTypes = {
  onInputChange: PropTypes.func,
  onSelect: PropTypes.func,
  onClose: PropTypes.func,
  userId: PropTypes.string,
  userName: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default SearchUserAutocomplete;
